import {
  ChartKind,
  IMeasureControl,
  IMeasureDisplayFormatFieldsFragment,
  IReportSettingFieldsFragment,
  Maybe,
  MeasureDisplayFormatKind,
  Order,
} from '../../../utils/apollo/graphqlSchema';
import {
  ChartNumberType,
  ChartSortEnum,
  ICentralYAxisSettings,
  IChartSettings,
  IDisplayedMeasureSetting,
  IDisplayedSliceSetting,
  IGeoMapConfig,
  IGeoMapTransform,
  ISliceFieldsFragment,
  ITableSettings,
  TableSortType,
  YAxisPosition,
} from '../../../components/Chart/interfaces';
import { CHART_TYPES } from '../../../components/Chart/constants/chartTypes';
import { UnreachableCaseError } from 'ts-essentials';
import { getAllSlices } from '../../../utils/redux/util';
import { COMPARISON_KEY_SUFFIX } from '../../../config/constants';
import { CHART_GROUPS_DEFAULT_COUNT } from '../../../components/Chart/constants/utils';
import { generateFunnelDefaultSettings } from './funnel';
import { generateExpressionDefaultSettings } from './expression';
import { IAnalysisResponse } from '../../../utils/apollo/responseTypes';
import { generateDataTableDefaultSettings } from './dataTable';
import { generatePathDefaultSettings } from './path';
import { generateRetentionDefaultSettings } from './retention';
import { IIncidentType } from '../../../pages/Incidents/interfaces';
import { freeze } from '@reduxjs/toolkit';
import produce from 'immer';
import { defaultMeasureDisplayFormat } from '../../../utils/builder/measures/utils';

export enum ChartViewOptions {
  ChartAndTable = 'chart-and-table',
  Chart = 'chart',
  Table = 'table',
}

export const defaultTableSettings = freeze<ITableSettings>(
  {
    chartView: ChartViewOptions.ChartAndTable,
    columnWidths: null,
    searchTerm: '',
    sortColumnIndex: null,
    sortDirection: Order.Asc,
    sortType: TableSortType.ALPHABETICAL,
    columnMapping: null,
    showHorizontalBars: false,
    filterConfig: null,
    sortConfig: null,
    showPercentDelta: false,
  },
  true,
);

export const defaultGeoMapTransform = freeze<IGeoMapTransform>(
  {
    x: 0,
    y: 0,
    k: 1,
  },
  true,
);

export const initialGeoMapConfig = freeze<IGeoMapConfig>(
  {
    ...defaultGeoMapTransform,
    isHeatmap: false,
    displayedMeasureKey: null,
  },
  true,
);

export const initialYAxisSettings = freeze<ICentralYAxisSettings>(
  {
    isApplyYAxisFormat: false,
    format: {
      kind: MeasureDisplayFormatKind.Number,
      decimalPlace: 2,
    },
    isTruncate: false,
    isVisible: true,
    label: null,
    bounds: null,
  },
  true,
);

export const initialChartSettings = freeze<IChartSettings>(
  {
    activeIncidentTypes: {},
    chartType: CHART_TYPES.LINE_CHART,
    displayedMeasureSettings: [],
    displayedSliceSettings: [],
    singleDayLabel: '',
    isConversionView: false,
    isShowFunnelConversionLabels: true,
    isShowFunnelBounceLabels: false,
    isFunnelRelativeLabels: true,
    isFunnelClusterMode: false,
    chartNumberType: ChartNumberType.Number,
    isChurn: false,
    sort: ChartSortEnum.NONE,
    displaySeq: 0,
    yAxisSettings: {
      [YAxisPosition.LEFT]: {
        isTruncateYAxis: false,
        yAxisCustomization: null,
      },
      [YAxisPosition.RIGHT]: {
        isTruncateYAxis: false,
        yAxisCustomization: null,
      },
    },
    pathEventsPerStep: 4,
    pathNodeSettings: [],
    pathObservedNodeId: null,
    expressionSettings: null,
    tableSettings: defaultTableSettings,
    topBreakdownsValue: CHART_GROUPS_DEFAULT_COUNT,
    geoMapConfig: initialGeoMapConfig,
    axisSettings: {
      yAxisSettings: {
        [YAxisPosition.LEFT]: initialYAxisSettings,
        [YAxisPosition.RIGHT]: initialYAxisSettings,
      },
      xAxisSettings: {
        isVisible: true,
        label: null,
      },
    },
  },
  true,
);

export const mapMeasureControl = ({
  measureControl,
  format,
  decimalPlace,
}: {
  measureControl: IMeasureControl;
  format: IMeasureDisplayFormatFieldsFragment | null;
  // if true, apply decimal format to the measure,
  // Used for result measure with division formula.
  decimalPlace?: number;
}): IDisplayedMeasureSetting => {
  const { key } = measureControl;
  const updatedFormat =
    format ??
    (decimalPlace
      ? {
          ...defaultMeasureDisplayFormat,
          kind: MeasureDisplayFormatKind.Number,
          decimalPlace,
        }
      : null);
  return {
    key,
    format: updatedFormat,
    yAxisPosition: YAxisPosition.LEFT,
    isVisible: false,
  };
};

export const mapDefaultChartKindToChartType = (
  chartKind: ChartKind,
): CHART_TYPES => {
  switch (chartKind) {
    case ChartKind.Line:
      return CHART_TYPES.LINE_CHART;
    case ChartKind.Bar:
      return CHART_TYPES.BAR_CHART;
    case ChartKind.PercentStackedArea:
      return CHART_TYPES.PERCENT_STACKED_AREA_CHART;
    case ChartKind.StackedColumn:
      return CHART_TYPES.STACKED_COLUMN_CHART;
    case ChartKind.Funnel:
      return CHART_TYPES.FUNNEL_CHART;
    case ChartKind.ComparativeLine:
      return CHART_TYPES.COMPARATIVE_LINE_CHART;
    case ChartKind.Geo:
      return CHART_TYPES.GEO_MAP;
    default:
      throw new UnreachableCaseError(chartKind);
  }
};

export const generateDisplayedSliceSettings = <T extends ISliceFieldsFragment>({
  analysis,
  visibleCount,
}: {
  analysis: {
    data?: {
      base: Maybe<T>;
      slices: T[];
    };
  };
  visibleCount?: number;
}): IDisplayedSliceSetting[] => {
  const slices = getAllSlices(analysis);
  return (
    slices
      // KBT-840 filter out comparison slices
      .filter((slice) => !slice.key.endsWith(COMPARISON_KEY_SUFFIX))
      .map((slice, sliceIndex) => ({
        key: slice.key,
        isVisible: sliceIndex < (visibleCount ?? CHART_GROUPS_DEFAULT_COUNT),
      }))
  );
};

export const generateDefaultChartSettings = (
  analysis: IAnalysisResponse,
  reportSetting: Maybe<IReportSettingFieldsFragment>,
  defaultActiveIncidentTypes: Record<IIncidentType['id'], boolean>,
): IChartSettings => {
  const defaultChartSettings = produce(initialChartSettings, (draft) => {
    // -----------------------------------------------------
    // Analyses which are migrated to the new Chart settings
    // -----------------------------------------------------
    if (analysis.__typename === 'Expression') {
      Object.assign(draft, generateExpressionDefaultSettings(analysis));
    } else if (analysis.__typename === 'Funnel') {
      Object.assign(
        draft,
        generateFunnelDefaultSettings(analysis, reportSetting),
      );
    } else if (analysis.__typename === 'Retention') {
      Object.assign(draft, generateRetentionDefaultSettings(analysis));
    } else if (analysis.__typename === 'Path') {
      Object.assign(draft, generatePathDefaultSettings(analysis));
    } else if (analysis.__typename === 'Prediction') {
      draft.chartType = CHART_TYPES.PREDICTION;
    } else if (analysis.__typename === 'CohortSample') {
      draft.chartType = CHART_TYPES.COHORT_SAMPLE;
    } else if (analysis.__typename === 'DataTable') {
      Object.assign(draft, generateDataTableDefaultSettings(analysis));
    } else if (analysis.__typename === 'Significance') {
      draft.chartType = CHART_TYPES.SIGNIFICANCE;
    } else {
      throw new UnreachableCaseError(analysis);
    }
  });

  return {
    ...defaultChartSettings,
    activeIncidentTypes: defaultActiveIncidentTypes,
  };
};
